import {
  OUTCOMES,
  ROOT_CAUSES,
  type JudgeOutcome,
  type JudgePeriodParams,
  type JudgeRootCause,
  type JudgeVerdictsParams,
} from '../../services/api/judge';
import { addDays } from './date-range';

import { BAD_OUTCOMES } from './kvalitet-labels';

const DEFAULT_DAYS = 30;
const MAX_DAYS = 365;
const PROMPT_VERSION_MAX = 100;

export const KVALITET_PRESETS = [
  { key: '1', label: 'Idag', days: 0 },
  { key: '7', label: '7 dagar', days: 6 },
  { key: '30', label: '30 dagar', days: 29 },
  { key: '90', label: '90 dagar', days: 89 },
] as const;

export const KVALITET_PARAM_KEYS = [
  'from',
  'to',
  'judgedFrom',
  'judgedTo',
  'outcome',
  'rootCause',
  'promptVersion',
] as const;

export type KvalitetState = {
  from: string;
  to: string;
  judged: { from: string; to: string } | null;
  outcomes: JudgeOutcome[];
  rootCause: JudgeRootCause | null;
  promptVersion: string | null;
};

// addDays(x, 0) går bara tillbaka till samma sträng för datum som finns: 2026-02-30 blir 03-02.
const isDate = (value: string | null): value is string =>
  value !== null && /^\d{4}-\d{2}-\d{2}$/.test(value) && addDays(value, 0) === value;

// Samma form som servern tar emot: datum, klockslag med sekunder och tidszon.
const isInstant = (value: string | null): value is string =>
  value !== null &&
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})$/.test(value) &&
  Number.isFinite(Date.parse(value));

export const isRootCause = (value: string | null): value is JudgeRootCause =>
  value !== null && (ROOT_CAUSES as readonly string[]).includes(value);

const spanDays = (from: string, to: string): number =>
  (Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000 + 1;

const normalizePeriod = (from: string, to: string): { from: string; to: string } => {
  const [first, last] = from <= to ? [from, to] : [to, from];
  return spanDays(first, last) > MAX_DAYS
    ? { from: addDays(last, -(MAX_DAYS - 1)), to: last }
    : { from: first, to: last };
};

const ALL_OUTCOMES = 'all';

// Listan visar de flaggade sessionerna tills någon väljer annat, därför är "bad"
// förvalet och "all" det uttryckliga valet att inte filtrera alls.
const parseOutcomes = (raw: string | null): JudgeOutcome[] => {
  const parts = (raw ?? '').split(',').map((part) => part.trim());
  if (parts.includes(ALL_OUTCOMES)) return [];
  const wanted = new Set(
    parts.flatMap((part): readonly string[] => (part === 'bad' ? BAD_OUTCOMES : [part])),
  );
  const outcomes = OUTCOMES.filter((outcome) => wanted.has(outcome));
  return outcomes.length > 0
    ? outcomes
    : OUTCOMES.filter((outcome) => BAD_OUTCOMES.includes(outcome));
};

const sameSet = (a: readonly string[], b: readonly string[]): boolean =>
  a.length === b.length && a.every((value) => b.includes(value));

const serializeOutcomes = (outcomes: readonly JudgeOutcome[]): string => {
  if (outcomes.length === 0) return ALL_OUTCOMES;
  if (sameSet(outcomes, BAD_OUTCOMES)) return 'bad';
  return OUTCOMES.filter((outcome) => outcomes.includes(outcome)).join(',');
};

export const parseKvalitetState = (params: URLSearchParams, today: string): KvalitetState => {
  const rawTo = params.get('to');
  const rawFrom = params.get('from');
  const to = isDate(rawTo) ? rawTo : today;
  const from = isDate(rawFrom) ? rawFrom : addDays(to, -(DEFAULT_DAYS - 1));

  const judgedFrom = params.get('judgedFrom');
  const judgedTo = params.get('judgedTo');
  const judged =
    isInstant(judgedFrom) && isInstant(judgedTo) && Date.parse(judgedFrom) < Date.parse(judgedTo)
      ? { from: judgedFrom, to: judgedTo }
      : null;

  const rootCause = params.get('rootCause');
  const promptVersion = params.get('promptVersion')?.trim() ?? '';

  return {
    ...normalizePeriod(from, to),
    judged,
    outcomes: parseOutcomes(params.get('outcome')),
    rootCause: isRootCause(rootCause) ? rootCause : null,
    promptVersion:
      promptVersion.length > 0 && promptVersion.length <= PROMPT_VERSION_MAX ? promptVersion : null,
  };
};

export type KvalitetPatch = {
  period?: { from: string; to: string };
  clearJudged?: boolean;
  outcomes?: readonly JudgeOutcome[];
  rootCause?: JudgeRootCause | null;
  promptVersion?: string | null;
};

const setOrDelete = (params: URLSearchParams, key: string, value: string | null) => {
  if (value) params.set(key, value);
  else params.delete(key);
};

// Returnerar en kopia: useSearchParams ger samma instans mellan renderingar, och
// en mutation av den följer med in i nästa uppdatering.
export const withKvalitetPatch = (
  current: URLSearchParams,
  patch: KvalitetPatch,
): URLSearchParams => {
  const next = new URLSearchParams(current);
  if (patch.period) {
    const period = normalizePeriod(patch.period.from, patch.period.to);
    next.set('from', period.from);
    next.set('to', period.to);
  }
  if (patch.period || patch.clearJudged) {
    next.delete('judgedFrom');
    next.delete('judgedTo');
  }
  if (patch.outcomes) setOrDelete(next, 'outcome', serializeOutcomes(patch.outcomes));
  if (patch.rootCause !== undefined) setOrDelete(next, 'rootCause', patch.rootCause);
  if (patch.promptVersion !== undefined) setOrDelete(next, 'promptVersion', patch.promptVersion);
  return next;
};

export const isBadSelection = (outcomes: readonly JudgeOutcome[]): boolean =>
  sameSet(outcomes, BAD_OUTCOMES);

export const activePresetKey = (state: KvalitetState, today: string): string | null =>
  state.judged || state.to !== today
    ? null
    : (KVALITET_PRESETS.find((preset) => state.from === addDays(today, -preset.days))?.key ?? null);

export const toPeriodParams = (state: KvalitetState): JudgePeriodParams => ({
  ...(state.judged
    ? { judgedFrom: state.judged.from, judgedTo: state.judged.to }
    : { from: state.from, to: state.to }),
  ...(state.promptVersion ? { promptVersion: state.promptVersion } : {}),
});

export const toVerdictParams = (
  state: KvalitetState,
): Omit<JudgeVerdictsParams, 'limit' | 'cursor'> => ({
  ...toPeriodParams(state),
  ...(state.outcomes.length > 0 ? { outcome: state.outcomes.join(',') } : {}),
  ...(state.rootCause ? { rootCause: state.rootCause } : {}),
});
